import type {
  MultiAgentSettingsState,
  ExecutionProfileDraft,
  ExecutionProfileRecord,
  ValidationError,
  MultiAgentSettingsTab,
  MultiAgentSettingsProfileTab,
  ModelProviderInfo,
  ToolGroup,
  ExecutionLimits,
  ArchitectureType,
  ProfileAgent,
} from "../../views/multi-agent/types";
// JAISIU-types-sweep: `parseProfileRecord` annotated its parameter with
// `AgentListEntryRecord` without ever importing it (the symbol resolved
// nowhere in this module). `controllers/agents-config.ts` is where that
// config-side `agents.list[]` record is declared.
import type { AgentListEntryRecord } from "../agents-config.ts";

export type MultiAgentSettingsHost = {
  // State
  maSettings: MultiAgentSettingsState;

  // Profile CRUD
  onMaProfilesLoad: () => void;
  onMaProfileSelect: (id: string | null) => void;
  onMaProfileNew: () => void;
  onMaProfileDelete: (id: string) => void;
  onMaProfileDuplicate: (id: string) => void;

  // Draft editing
  onMaDraftUpdate: (patch: Partial<ExecutionProfileDraft>) => void;
  onMaDraftAgentAdd: (agentId: string) => void;
  onMaDraftAgentRemove: (agentId: string) => void;
  onMaDraftAgentReorder: (from: number, to: number) => void;
  onMaDraftSave: () => void;
  onMaDraftCancel: () => void;

  // Navigation
  onMaTabChange: (tab: MultiAgentSettingsTab) => void;
  onMaProfileTabChange: (tab: MultiAgentSettingsProfileTab) => void;
};

// ─── Initial State ─────────────────────────────────────────────────────────────

export const INITIAL_MA_SETTINGS: MultiAgentSettingsState = {
  profiles: [],
  profilesLoading: false,
  profilesError: null,
  draft: null,
  draftDirty: false,
  draftErrors: [],
  selectedProfileId: null,
  activeTab: "profiles",
  activeProfileTab: "basic",
  saving: false,
  saveError: null,
  deleting: false,
  deleteError: null,
  selectedNodeId: null,
};

// ─── API client ───────────────────────────────────────────────────────────────

const API_BASE = "/api/multi-agent-profiles";

async function apiFetch<T>(endpoint: string, options?: RequestInit): Promise<T> {
  const response = await fetch(
    `${endpoint.startsWith("http") ? endpoint : API_BASE}${endpoint.startsWith("http") ? "" : endpoint}`,
    {
      headers: {
        "Content-Type": "application/json",
        ...options?.headers,
      },
      ...options,
    },
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "Unknown error" }));
    throw new Error(error.error || `API error: ${response.status}`);
  }

  return response.json() as Promise<T>;
}

export interface ApiProfile {
  id: string;
  userId: string;
  name: string;
  description?: string;
  defaultModelId?: string;
  agents: ProfileAgent[];
  workspace?: Record<string, unknown>;
  settings: {
    maxConcurrency?: number;
    agentTimeoutMs?: number;
    interactionMode?: "sequential" | "parallel" | "pipeline";
    custom?: Record<string, unknown>;
  };
  tags: string[];
  createdAt: string;
  updatedAt: string;
  enabled: boolean;
}

// ─── Profile CRUD ──────────────────────────────────────────────────────────────

export async function maLoadProfiles(host: MultiAgentSettingsHost): Promise<void> {
  host.maSettings.profilesLoading = true;
  host.maSettings.profilesError = null;

  try {
    const apiProfiles = await apiFetch<ApiProfile[]>("/api/multi-agent-profiles");
    const profiles: ExecutionProfileRecord[] = apiProfiles.map((p) => apiProfileToRecord(p));
    host.maSettings.profiles = profiles;
  } catch (err) {
    host.maSettings.profilesError = String(err);
  } finally {
    host.maSettings.profilesLoading = false;
  }
}

/**
 * Convert API profile to UI record format.
 */
function apiProfileToRecord(p: ApiProfile): ExecutionProfileRecord {
  // Determine architecture type from interaction mode or infer from agents count
  let architectureType: ArchitectureType = "sequential";
  if (p.settings.interactionMode === "parallel") {
    architectureType = "parallel";
  } else if (p.settings.interactionMode === "pipeline" && p.agents.length >= 2) {
    // pipeline with multiple agents could be sequential or custom
    architectureType = "sequential";
  }

  return {
    id: p.id,
    name: p.name,
    description: p.description ?? "",
    architectureType,
    agents: p.agents.map((a) => ({
      agentId: a.agentId,
      order: a.order,
      modelOverride: a.modelOverride,
      additionalTools: a.additionalTools,
      enabled: a.enabled,
    })),
    modelProviderId: "default",
    defaultModelId: p.defaultModelId ?? "",
    limits: {
      maxIterations: 100,
      maxTokens: 100_000,
      maxCost: 10,
      maxDurationSeconds: 3600,
      maxConcurrentSubagents: p.settings.maxConcurrency ?? 4,
    },
    createdAt: typeof p.createdAt === "string" ? new Date(p.createdAt).getTime() : p.createdAt,
    updatedAt: typeof p.updatedAt === "string" ? new Date(p.updatedAt).getTime() : p.updatedAt,
    runCount: 0,
  };
}

export function maSelectProfile(host: MultiAgentSettingsHost, id: string | null): void {
  host.maSettings.selectedProfileId = id;

  if (id === null) {
    host.maSettings.draft = null;
    host.maSettings.draftDirty = false;
    host.maSettings.draftErrors = [];
    host.maSettings.activeTab = "profiles";
    host.maSettings.activeProfileTab = "basic";
    return;
  }

  const existing = host.maSettings.profiles.find((p) => p.id === id);
  if (existing) {
    host.maSettings.draft = profileToDraft(existing);
    host.maSettings.draftDirty = false;
    host.maSettings.draftErrors = validateDraft(host.maSettings.draft);
    host.maSettings.activeTab = "profiles";
    host.maSettings.activeProfileTab = "basic";
  }
}

export function maNewProfile(host: MultiAgentSettingsHost): void {
  host.maSettings.selectedProfileId = null;
  host.maSettings.draft = {
    name: "New Profile",
    description: "",
    architectureType: "sequential",
    agentIds: [],
    modelProviderId: "default",
    defaultModelId: "",
    limits: {
      maxIterations: 100,
      maxTokens: 100_000,
      maxCost: 10,
      maxDurationSeconds: 3600,
      maxConcurrentSubagents: 4,
    },
    agentOverrides: {},
  };
  host.maSettings.draftDirty = false;
  host.maSettings.draftErrors = [];
  host.maSettings.activeTab = "profiles";
  host.maSettings.activeProfileTab = "basic";
}

export async function maDeleteProfile(host: MultiAgentSettingsHost, id: string): Promise<void> {
  host.maSettings.deleting = true;
  host.maSettings.deleteError = null;

  try {
    await apiFetch(`/api/multi-agent-profiles/${id}`, { method: "DELETE" });
    host.maSettings.profiles = host.maSettings.profiles.filter((p) => p.id !== id);
    if (host.maSettings.selectedProfileId === id) {
      host.maSettings.selectedProfileId = null;
      host.maSettings.draft = null;
      host.maSettings.activeTab = "profiles";
    }
  } catch (err) {
    host.maSettings.deleteError = String(err);
  } finally {
    host.maSettings.deleting = false;
  }
}

/**
 * Seed example data for the current user.
 * POST /api/multi-agent/seed
 */
export async function maSeedExampleData(host: MultiAgentSettingsHost): Promise<void> {
  host.maSettings.profilesLoading = true;
  host.maSettings.profilesError = null;

  try {
    await apiFetch("/api/multi-agent/seed", { method: "POST" });
    // Reload profiles after seeding
    await maLoadProfiles(host);
  } catch (err) {
    host.maSettings.profilesError = String(err);
    host.maSettings.profilesLoading = false;
  }
}

export function maDuplicateProfile(host: MultiAgentSettingsHost, id: string): void {
  const original = host.maSettings.profiles.find((p) => p.id === id);
  if (!original) return;

  // Create a new draft without an ID (will be assigned by API on save)
  host.maSettings.draft = {
    ...profileToDraft(original),
    id: undefined,
    name: `${original.name} (copy)`,
  };
  host.maSettings.draftDirty = true;
  host.maSettings.draftErrors = [];
  host.maSettings.selectedProfileId = null;
  host.maSettings.activeTab = "profiles";
  host.maSettings.activeProfileTab = "basic";
}

// ─── Draft Editing ────────────────────────────────────────────────────────────

export function maUpdateDraft(
  host: MultiAgentSettingsHost,
  patch: Partial<ExecutionProfileDraft>,
): void {
  if (!host.maSettings.draft) return;
  host.maSettings.draft = { ...host.maSettings.draft, ...patch };
  host.maSettings.draftDirty = true;
  host.maSettings.draftErrors = validateDraft(host.maSettings.draft);
}

export function maAddAgent(host: MultiAgentSettingsHost, agentId: string): void {
  if (!host.maSettings.draft) return;
  if (host.maSettings.draft.agentIds.includes(agentId)) return;
  host.maSettings.draft = {
    ...host.maSettings.draft,
    agentIds: [...host.maSettings.draft.agentIds, agentId],
  };
  host.maSettings.draftDirty = true;
  host.maSettings.draftErrors = validateDraft(host.maSettings.draft);
}

export function maRemoveAgent(host: MultiAgentSettingsHost, agentId: string): void {
  if (!host.maSettings.draft) return;
  host.maSettings.draft = {
    ...host.maSettings.draft,
    agentIds: host.maSettings.draft.agentIds.filter((id) => id !== agentId),
  };
  host.maSettings.draftDirty = true;
  host.maSettings.draftErrors = validateDraft(host.maSettings.draft);
}

export function maReorderAgents(host: MultiAgentSettingsHost, from: number, to: number): void {
  if (!host.maSettings.draft) return;
  const ids = [...host.maSettings.draft.agentIds];
  const [moved] = ids.splice(from, 1);
  ids.splice(to, 0, moved);
  host.maSettings.draft = { ...host.maSettings.draft, agentIds: ids };
  host.maSettings.draftDirty = true;
}

export async function maSaveDraft(host: MultiAgentSettingsHost): Promise<void> {
  if (!host.maSettings.draft) return;

  const errors = validateDraft(host.maSettings.draft);
  if (errors.length > 0) {
    host.maSettings.draftErrors = errors;
    return;
  }

  host.maSettings.saving = true;
  host.maSettings.saveError = null;

  try {
    const record = draftToProfileRecord(host.maSettings.draft);
    const isNew = !record.id || !host.maSettings.profiles.find((p) => p.id === record.id);

    // Convert to API format
    const apiPayload = recordToApiPayload(record);

    let saved: ApiProfile;
    if (isNew) {
      saved = await apiFetch<ApiProfile>("/api/multi-agent-profiles", {
        method: "POST",
        body: JSON.stringify(apiPayload),
      });
    } else {
      saved = await apiFetch<ApiProfile>(`/api/multi-agent-profiles/${record.id}`, {
        method: "PUT",
        body: JSON.stringify(apiPayload),
      });
    }

    const savedRecord = apiProfileToRecord(saved);
    const existing = host.maSettings.profiles.findIndex((p) => p.id === savedRecord.id);
    if (existing >= 0) {
      host.maSettings.profiles = [
        ...host.maSettings.profiles.slice(0, existing),
        savedRecord,
        ...host.maSettings.profiles.slice(existing + 1),
      ];
    } else {
      host.maSettings.profiles = [...host.maSettings.profiles, savedRecord];
    }
    host.maSettings.selectedProfileId = savedRecord.id;
    host.maSettings.draft = profileToDraft(savedRecord);
    host.maSettings.draftDirty = false;
    host.maSettings.draftErrors = [];
  } catch (err) {
    host.maSettings.saveError = String(err);
  } finally {
    host.maSettings.saving = false;
  }
}

/**
 * Convert UI record to API payload format.
 */
function recordToApiPayload(record: ExecutionProfileRecord): Record<string, unknown> {
  // Determine interaction mode from architecture type
  let interactionMode: "sequential" | "parallel" | "pipeline" = "pipeline";
  if (record.architectureType === "parallel") {
    interactionMode = "parallel";
  } else if (record.architectureType === "sequential") {
    interactionMode = "sequential";
  }

  return {
    name: record.name,
    description: record.description,
    defaultModelId: record.defaultModelId || undefined,
    agents: record.agents.map((a, i) => ({
      agentId: a.agentId,
      order: a.order ?? i,
      enabled: a.enabled ?? true,
      permissions: {},
      modelOverride: a.modelOverride,
      additionalTools: a.additionalTools,
    })),
    settings: {
      maxConcurrency: record.limits.maxConcurrentSubagents ?? 4,
      agentTimeoutMs: record.limits.maxDurationSeconds
        ? record.limits.maxDurationSeconds * 1000
        : 120000,
      interactionMode,
    },
    tags: [],
    enabled: true,
  };
}

export function maCancelDraft(host: MultiAgentSettingsHost): void {
  if (host.maSettings.selectedProfileId) {
    const existing = host.maSettings.profiles.find(
      (p) => p.id === host.maSettings.selectedProfileId,
    );
    if (existing) {
      host.maSettings.draft = profileToDraft(existing);
    }
  } else {
    host.maSettings.draft = null;
  }
  host.maSettings.draftDirty = false;
  host.maSettings.draftErrors = [];
}

// ─── Validation ────────────────────────────────────────────────────────────────

export function validateDraft(draft: ExecutionProfileDraft | null): ValidationError[] {
  if (!draft) return [];
  const errors: ValidationError[] = [];

  if (!draft.name.trim()) {
    errors.push({ field: "name", message: "Profile name is required." });
  }

  if (draft.agentIds.length === 0 && draft.architectureType !== "custom") {
    errors.push({
      field: "agents",
      message: "At least one agent is required for this architecture type.",
    });
  }

  if (draft.architectureType === "supervisor" && draft.agentIds.length < 2) {
    errors.push({
      field: "agents",
      message: "Supervisor architecture requires at least 2 agents (supervisor + subordinates).",
      suggestion: "Add a supervisor agent and at least one subordinate.",
    });
  }

  if (draft.architectureType === "parallel" && draft.agentIds.length < 2) {
    errors.push({
      field: "agents",
      message: "Parallel architecture requires at least 2 parallel agents.",
    });
  }

  if ((draft.limits.maxCost ?? 0) <= 0) {
    errors.push({ field: "limits.maxCost", message: "Max cost must be greater than 0." });
  }

  if ((draft.limits.maxDurationSeconds ?? 0) <= 0) {
    errors.push({
      field: "limits.maxDurationSeconds",
      message: "Max duration must be greater than 0.",
    });
  }

  if ((draft.limits.maxConcurrentSubagents ?? 0) < 1) {
    errors.push({
      field: "limits.maxConcurrentSubagents",
      message: "Must allow at least 1 concurrent subagent.",
    });
  }

  return errors;
}

// ─── Conversions ───────────────────────────────────────────────────────────────

function parseProfileRecord(r: AgentListEntryRecord): ExecutionProfileRecord | null {
  const id = r.id;
  if (!id?.startsWith("profile:")) return null;

  const meta = (r as unknown as { _meta?: Record<string, unknown> })._meta ?? {};

  return {
    id,
    name: r.name ?? "Unnamed Profile",
    description: r.workspace ?? "",
    architectureType: ((meta.architectureType as ArchitectureType) ??
      "sequential") as ArchitectureType,
    agents: (meta.agents as ProfileAgent[]) ?? [],
    modelProviderId: (meta.modelProviderId as string) ?? "default",
    defaultModelId: (meta.defaultModelId as string) ?? "",
    limits: (meta.limits as ExecutionLimits) ?? {
      maxIterations: 100,
      maxTokens: 100_000,
      maxCost: 10,
      maxDurationSeconds: 3600,
      maxConcurrentSubagents: 4,
    },
    createdAt: (meta.createdAt as number) ?? Date.now(),
    updatedAt: (meta.updatedAt as number) ?? Date.now(),
    lastUsedAt: meta.lastUsedAt as number | undefined,
    runCount: (meta.runCount as number) ?? 0,
  };
}

export function profileToDraft(p: ExecutionProfileRecord): ExecutionProfileDraft {
  return {
    id: p.id,
    name: p.name,
    description: p.description,
    architectureType: p.architectureType,
    agentIds: p.agents.map((a) => a.agentId),
    modelProviderId: p.modelProviderId ?? "default",
    defaultModelId: p.defaultModelId ?? "",
    limits: { ...p.limits },
    agentOverrides: {},
  };
}

export function draftToProfileRecord(draft: ExecutionProfileDraft): ExecutionProfileRecord {
  const id = draft.id ?? `profile:${Date.now()}`;
  const now = Date.now();

  return {
    id,
    name: draft.name,
    description: draft.description,
    architectureType: draft.architectureType,
    agents: draft.agentIds.map((agentId, index) => ({
      agentId,
      role: draft.agentOverrides[agentId]?.role ?? (index === 0 ? "primary" : "worker"),
      modelId: draft.agentOverrides[agentId]?.modelId ?? draft.defaultModelId,
      fallbackModelIds: draft.agentOverrides[agentId]?.fallbackModelIds ?? [],
      permissions: draft.agentOverrides[agentId]?.permissions ?? {
        allow: [],
        deny: [],
        toolGroups: [],
      },
      executionLimits: draft.agentOverrides[agentId]?.executionLimits,
    })),
    modelProviderId: draft.modelProviderId,
    defaultModelId: draft.defaultModelId,
    limits: { ...draft.limits },
    createdAt: now,
    updatedAt: now,
    runCount: 0,
  };
}

// ─── Tool Groups (static) ─────────────────────────────────────────────────────

export const TOOL_GROUPS: ToolGroup[] = [
  {
    id: "filesystem",
    label: "File System",
    tools: [
      {
        id: "read",
        label: "Read Files",
        description: "Read file contents",
        dangerLevel: "safe",
        group: "filesystem",
      },
      {
        id: "write",
        label: "Write Files",
        description: "Create or overwrite files",
        dangerLevel: "moderate",
        group: "filesystem",
      },
      {
        id: "list",
        label: "List Directory",
        description: "List directory contents",
        dangerLevel: "safe",
        group: "filesystem",
      },
      {
        id: "delete",
        label: "Delete Files",
        description: "Delete files or directories",
        dangerLevel: "dangerous",
        group: "filesystem",
      },
      {
        id: "mkdir",
        label: "Make Directory",
        description: "Create directories",
        dangerLevel: "moderate",
        group: "filesystem",
      },
    ],
  },
  {
    id: "runtime",
    label: "Runtime & Execution",
    tools: [
      {
        id: "exec",
        label: "Shell Command",
        description: "Run shell commands",
        dangerLevel: "dangerous",
        group: "runtime",
      },
      {
        id: "npm",
        label: "NPM/Package",
        description: "Run npm/pnpm commands",
        dangerLevel: "dangerous",
        group: "runtime",
      },
      {
        id: "git",
        label: "Git Operations",
        description: "Run git commands",
        dangerLevel: "moderate",
        group: "runtime",
      },
    ],
  },
  {
    id: "sessions",
    label: "Sessions & Agents",
    tools: [
      {
        id: "sessions_spawn",
        label: "Spawn Agents",
        description: "Spawn sub-agents",
        dangerLevel: "moderate",
        group: "sessions",
      },
      {
        id: "sessions_list",
        label: "List Sessions",
        description: "List active sessions",
        dangerLevel: "safe",
        group: "sessions",
      },
    ],
  },
  {
    id: "memory",
    label: "Memory",
    tools: [
      {
        id: "memory_read",
        label: "Read Memory",
        description: "Read from memory store",
        dangerLevel: "safe",
        group: "memory",
      },
      {
        id: "memory_write",
        label: "Write Memory",
        description: "Write to memory store",
        dangerLevel: "moderate",
        group: "memory",
      },
      {
        id: "memory_search",
        label: "Search Memory",
        description: "Semantic search memory",
        dangerLevel: "safe",
        group: "memory",
      },
    ],
  },
  {
    id: "web",
    label: "Web Access",
    tools: [
      {
        id: "web_search",
        label: "Web Search",
        description:
          "Search the web via Brave or Perplexity. Configure provider and API key at Config → Tools → Web Search (tools.web.search).",
        dangerLevel: "safe",
        group: "web",
      },
      {
        id: "web_fetch",
        label: "Web Fetch",
        description: "Fetch web pages",
        dangerLevel: "moderate",
        group: "web",
      },
      {
        id: "browser",
        label: "Browser Control",
        description: "Control a browser",
        dangerLevel: "moderate",
        group: "web",
      },
    ],
  },
  {
    id: "messaging",
    label: "Messaging",
    tools: [
      {
        id: "message_send",
        label: "Send Message",
        description: "Send messages via channels",
        dangerLevel: "moderate",
        group: "messaging",
      },
      {
        id: "email_send",
        label: "Send Email",
        description: "Send emails",
        dangerLevel: "moderate",
        group: "messaging",
      },
    ],
  },
];

// ─── Model Providers (static) ───────────────────────────────────────────────────

export const STATIC_MODEL_PROVIDERS: ModelProviderInfo[] = [
  {
    id: "default",
    name: "Default Provider",
    status: "connected",
    defaultModelId: "claude-3-5-sonnet-20241022",
    availableModels: [
      {
        id: "claude-3-5-sonnet-20241022",
        label: "Claude 3.5 Sonnet",
        provider: "anthropic",
        supportsStreaming: true,
        supportsVision: true,
        contextWindow: 200_000,
        costPer1kInput: 0.003,
        costPer1kOutput: 0.015,
      },
      {
        id: "claude-3-opus-20240229",
        label: "Claude 3 Opus",
        provider: "anthropic",
        supportsStreaming: true,
        supportsVision: true,
        contextWindow: 200_000,
        costPer1kInput: 0.015,
        costPer1kOutput: 0.075,
      },
    ],
  },
  {
    id: "openai",
    name: "OpenAI",
    status: "unconfigured",
    defaultModelId: "gpt-4o",
    availableModels: [
      {
        id: "gpt-4o",
        label: "GPT-4o",
        provider: "openai",
        supportsStreaming: true,
        supportsVision: true,
        contextWindow: 128_000,
        costPer1kInput: 0.005,
        costPer1kOutput: 0.015,
      },
    ],
  },
  {
    id: "google",
    name: "Google AI",
    status: "unconfigured",
    defaultModelId: "gemini-1.5-pro",
    availableModels: [
      {
        id: "gemini-1.5-pro",
        label: "Gemini 1.5 Pro",
        provider: "google",
        supportsStreaming: true,
        supportsVision: true,
        contextWindow: 2_000_000,
        costPer1kInput: 0.00125,
        costPer1kOutput: 0.005,
      },
    ],
  },
];
